"use client"

import React, { useState, useEffect } from "react"
import { useLanguage } from "../contexts/LanguageContext"
import { useToast } from "../components/ui/use-toast"
import { bannerAPI, Banner, CreateBannerDTO } from "../services/marketingService"
import PageHeader from "../components/ui/dashboard/PageHeader"
import { Plus, Search, Image as ImageIcon, Calendar, Eye, EyeOff, Edit, Trash2, Upload } from "lucide-react"
import {
    Tabs, Card, Flex, Text, Button, Badge, Table, Dialog, TextField, Select, Switch, TextArea
} from "@radix-ui/themes"

const BannersPage: React.FC = () => {
    const { t } = useLanguage()
    const { toast } = useToast()
    const [activeTab, setActiveTab] = useState("all")

    // State
    const [banners, setBanners] = useState<Banner[]>([])
    const [loading, setLoading] = useState(true)
    const [showDialog, setShowDialog] = useState(false)
    const [selectedBanner, setSelectedBanner] = useState<Banner | null>(null)
    const [searchQuery, setSearchQuery] = useState("")

    // Form State
    const [formData, setFormData] = useState<CreateBannerDTO>({
        title: "",
        description: "",
        imageUrl: "",
        targetUrl: "",
        placement: "home",
        isActive: true,
        startDate: "",
        endDate: "",
        priority: 1
    })

    // Fetch Banners
    const fetchBanners = async () => {
        setLoading(true)
        try {
            const response = await bannerAPI.getBanners()
            setBanners(response.data.data || [])
        } catch (error) {
            console.error("Failed to load banners", error)
            // Mock data for development
            setBanners([
                {
                    _id: "1",
                    title: "Summer Sale",
                    description: "Get 30% off on all orders",
                    imageUrl: "https://via.placeholder.com/800x400",
                    placement: "home",
                    isActive: true,
                    startDate: "2024-01-01",
                    endDate: "2024-12-31",
                    priority: 1,
                    createdAt: "2024-01-01",
                    updatedAt: "2024-01-01"
                }
            ])
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchBanners()
    }, [])

    const handleSave = async () => {
        try {
            if (selectedBanner) {
                await bannerAPI.updateBanner(selectedBanner._id, formData)
                toast({ title: t("success"), description: "Banner updated successfully" })
            } else {
                await bannerAPI.createBanner(formData)
                toast({ title: t("success"), description: "Banner created successfully" })
            }
            setShowDialog(false)
            resetForm()
            fetchBanners()
        } catch (error) {
            toast({ title: t("error"), description: "Failed to save banner", variant: "destructive" })
        }
    }

    const handleDelete = async (id: string) => {
        if (!window.confirm("Are you sure you want to delete this banner?")) return
        try {
            await bannerAPI.deleteBanner(id)
            toast({ title: t("success"), description: "Banner deleted successfully" })
            fetchBanners()
        } catch (error) {
            toast({ title: t("error"), description: "Failed to delete banner", variant: "destructive" })
        }
    }

    const handleToggleStatus = async (id: string, isActive: boolean) => {
        try {
            await bannerAPI.toggleBannerStatus(id, !isActive)
            toast({ title: t("success"), description: `Banner ${!isActive ? "activated" : "deactivated"}` })
            fetchBanners()
        } catch (error) {
            toast({ title: t("error"), description: "Failed to update status", variant: "destructive" })
        }
    }

    const handleEdit = (banner: Banner) => {
        setSelectedBanner(banner)
        setFormData({
            title: banner.title,
            description: banner.description,
            imageUrl: banner.imageUrl,
            targetUrl: banner.targetUrl,
            placement: banner.placement,
            isActive: banner.isActive,
            startDate: banner.startDate.split('T')[0],
            endDate: banner.endDate.split('T')[0],
            priority: banner.priority
        })
        setShowDialog(true)
    }

    const handleAddNew = () => {
        setSelectedBanner(null)
        resetForm()
        setShowDialog(true)
    }

    const resetForm = () => {
        setFormData({
            title: "",
            description: "",
            imageUrl: "",
            targetUrl: "",
            placement: "home",
            isActive: true,
            startDate: "",
            endDate: "",
            priority: 1
        })
    }

    const filteredBanners = banners.filter(banner => {
        const matchesSearch = banner.title.toLowerCase().includes(searchQuery.toLowerCase())
        const matchesTab = activeTab === "all" || banner.placement === activeTab
        return matchesSearch && matchesTab
    })

    return (
        <div className="space-y-6">
            <PageHeader
                title={t("bannerManagement") || "Banner Management"}
                subtitle={t("manageBannersDesc") || "Upload and schedule promotional banners"}
            />

            <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
                <Tabs.List>
                    <Tabs.Trigger value="all">{t("all") || "All"}</Tabs.Trigger>
                    <Tabs.Trigger value="home">{t("home") || "Home"}</Tabs.Trigger>
                    <Tabs.Trigger value="restaurant">{t("restaurant") || "Restaurant"}</Tabs.Trigger>
                    <Tabs.Trigger value="checkout">{t("checkout") || "Checkout"}</Tabs.Trigger>
                    <Tabs.Trigger value="profile">{t("profile") || "Profile"}</Tabs.Trigger>
                </Tabs.List>

                <div className="mt-4">
                    <Card>
                        <Flex direction="column" gap="4">
                            <Flex justify="between" align="center">
                                <div className="relative w-64">
                                    <Search className="absolute top-1/2 -translate-y-1/2 left-3 w-4 h-4 text-gray-400" />
                                    <input
                                        placeholder={t("searchBanners") || "Search banners..."}
                                        className="w-full py-2 pl-10 pr-4 border rounded-lg border-gray-300 dark:border-gray-600 bg-transparent"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>
                                <Button onClick={handleAddNew}>
                                    <Plus className="w-4 h-4 mr-2" /> {t("addBanner") || "Add Banner"}
                                </Button>
                            </Flex>

                            <Table.Root variant="surface">
                                <Table.Header>
                                    <Table.Row>
                                        <Table.ColumnHeaderCell>{t("preview") || "Preview"}</Table.ColumnHeaderCell>
                                        <Table.ColumnHeaderCell>{t("title") || "Title"}</Table.ColumnHeaderCell>
                                        <Table.ColumnHeaderCell>{t("placement") || "Placement"}</Table.ColumnHeaderCell>
                                        <Table.ColumnHeaderCell>{t("schedule") || "Schedule"}</Table.ColumnHeaderCell>
                                        <Table.ColumnHeaderCell>{t("status") || "Status"}</Table.ColumnHeaderCell>
                                        <Table.ColumnHeaderCell>{t("actions") || "Actions"}</Table.ColumnHeaderCell>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {filteredBanners.map(banner => (
                                        <Table.Row key={banner._id}>
                                            <Table.Cell>
                                                <img src={banner.imageUrl} alt={banner.title} className="w-20 h-10 object-cover rounded" />
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Text weight="bold">{banner.title}</Text>
                                                {banner.description && <Text size="1" color="gray" className="block">{banner.description}</Text>}
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Badge color="blue">{banner.placement}</Badge>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Text size="1">{new Date(banner.startDate).toLocaleDateString()} - {new Date(banner.endDate).toLocaleDateString()}</Text>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Badge color={banner.isActive ? "green" : "gray"}>
                                                    {banner.isActive ? "Active" : "Inactive"}
                                                </Badge>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Flex gap="2">
                                                    <Button size="1" variant="soft" onClick={() => handleToggleStatus(banner._id, banner.isActive)}>
                                                        {banner.isActive ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                                    </Button>
                                                    <Button size="1" variant="soft" color="blue" onClick={() => handleEdit(banner)}>
                                                        <Edit className="w-3 h-3" />
                                                    </Button>
                                                    <Button size="1" variant="soft" color="red" onClick={() => handleDelete(banner._id)}>
                                                        <Trash2 className="w-3 h-3" />
                                                    </Button>
                                                </Flex>
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                    {filteredBanners.length === 0 && !loading && (
                                        <Table.Row>
                                            <Table.Cell colSpan={6} align="center">
                                                <Text color="gray">{t("noBannersFound") || "No banners found"}</Text>
                                            </Table.Cell>
                                        </Table.Row>
                                    )}
                                </Table.Body>
                            </Table.Root>
                        </Flex>
                    </Card>
                </div>
            </Tabs.Root>

            <Dialog.Root open={showDialog} onOpenChange={setShowDialog}>
                <Dialog.Content style={{ maxWidth: 550 }}>
                    <Dialog.Title>{selectedBanner ? (t("editBanner") || "Edit Banner") : (t("addBanner") || "Add Banner")}</Dialog.Title>
                    <Flex direction="column" gap="3">
                        <label>
                            <Text as="div" size="2" mb="1" weight="bold">{t("title") || "Title"}</Text>
                            <TextField.Root value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Summer Sale" required />
                        </label>
                        <label>
                            <Text as="div" size="2" mb="1" weight="bold">{t("description") || "Description"}</Text>
                            <TextField.Root value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Get 30% off..." />
                        </label>
                        <label>
                            <Text as="div" size="2" mb="1" weight="bold">{t("imageUrl") || "Image URL"}</Text>
                            <TextField.Root value={formData.imageUrl} onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })} placeholder="https://..." required />
                        </label>
                        <label>
                            <Text as="div" size="2" mb="1" weight="bold">{t("placement") || "Placement"}</Text>
                            <Select.Root value={formData.placement} onValueChange={(value: any) => setFormData({ ...formData, placement: value })}>
                                <Select.Trigger style={{ width: "100%" }} />
                                <Select.Content>
                                    <Select.Item value="home">Home</Select.Item>
                                    <Select.Item value="restaurant">Restaurant</Select.Item>
                                    <Select.Item value="checkout">Checkout</Select.Item>
                                    <Select.Item value="profile">Profile</Select.Item>
                                </Select.Content>
                            </Select.Root>
                        </label>
                        <Flex gap="3">
                            <label className="flex-1">
                                <Text as="div" size="2" mb="1" weight="bold">{t("startDate") || "Start Date"}</Text>
                                <TextField.Root type="date" value={formData.startDate} onChange={(e) => setFormData({ ...formData, startDate: e.target.value })} required />
                            </label>
                            <label className="flex-1">
                                <Text as="div" size="2" mb="1" weight="bold">{t("endDate") || "End Date"}</Text>
                                <TextField.Root type="date" value={formData.endDate} onChange={(e) => setFormData({ ...formData, endDate: e.target.value })} required />
                            </label>
                        </Flex>
                        <Flex gap="3" mt="4" justify="end">
                            <Dialog.Close>
                                <Button variant="soft" color="gray">{t("cancel") || "Cancel"}</Button>
                            </Dialog.Close>
                            <Button onClick={handleSave}>{t("save") || "Save"}</Button>
                        </Flex>
                    </Flex>
                </Dialog.Content>
            </Dialog.Root>
        </div>
    )
}

export default BannersPage
